import { dataLayerProvider, tracker } from '@femtasy/helpers';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppDispatch } from 'containers/Femtasy';

import analytics from 'helpers/analytics';
import { isProduction } from 'helpers/env';
import femtasyLocalStorage from 'helpers/femtasyLocalStorage';
import femtasySessionStorage from 'helpers/femtasySessionStorage';
import { metricsQueue } from 'helpers/metricsQueue';

import { sendSearchInsightBuilder } from 'hooks/useAlgolia';

import { GetReduxState } from 'store/ReduxState';

import { CurrentlyPlaying } from './../../components/ConnectedAudioPlayer/hls-helper';

// CONSTANTS
export const SEEK_DURATION = 15;
export const REQUIRED_STORY_DURATION = 60;
export const LESS_THAN_REQUIRED_DURATION = 'lt';
export const GREATER_THAN_REQUIRED_DURATION = 'gt';
export const PERCENTAGES_TO_TRACK = [1, 25, 50, 75, 100];
export const PLAYER_DURATION_LABEL_STORED = 'femtasy-app.PLAYER_END_TIME';
export const FEMTASY_AUTOPLAY = 'AUTOPLAY';

/* In order to not have to depend on Redux, we use this module level variable to keep track of the last percentage that was tracked.
 * This allow us to avoid tracking the same percentage multiple times. e.g. on a long audio a % could be the same for multiple seconds.
 * This variable is global and should be reset every time a new audio is played. The advantage we have is that there should only be one
 *  instance of the player at a time, so is save to keep this variable.
 */
let lastTrackedPercentage: number = 0;
let reached25Pct = false;
let loadStartTime: number | null = null;
let seekStartTime: number | null = null;
let userSeekPending = false;
let currentSeekIsUserInitiated = false;

const MEDIA_ERROR_TYPE: Record<number, string> = {
  1: 'aborted',
  2: 'network',
  3: 'decode',
  4: 'src_not_supported',
};

interface ITrackPlayerEventProps {
  eventName: string;
  category?: string;
  currentTime?: number;
  currentlyPlaying: any;
  isTeaser?: boolean;
}

export const trackPlayerEvent = ({
  eventName,
  category = 'player',
  currentTime,
  currentlyPlaying,
  isTeaser = false,
}: ITrackPlayerEventProps) => {
  if (!currentlyPlaying) {
    return;
  }
  const {
    speakerName,
    storyTitle,
    language_intensity,
    author,
    genre,
    free_trial,
  } = currentlyPlaying;
  const currentPosition = currentTime ? currentTime - (currentTime % 30) : 0;
  const authorInfo = author ? author.name : '';

  analytics.track(eventName, {
    category,
    label: storyTitle,
    value: currentPosition,
    Position: currentPosition,
    Speaker: speakerName,
    Intensity: language_intensity,
    FreeStory: free_trial,
    Author: authorInfo,
    Genre: genre,
    isTeaser: isTeaser,
  });

  trackAudioEvent({
    audioAction: eventName,
    audioName: storyTitle,
    audioSpeaker: speakerName,
    isTeaser,
    audioUrl: currentlyPlaying.audioUrl,
  });
};

// REDUCER
export interface PlayerState {
  audioLoaded: boolean;
  audioLoading: boolean;
  currentlyPlaying?: CurrentlyPlaying;
  currentTime: number;
  playing: boolean;
  seekTo?: number;
  seeking: boolean;
  fullAudioPlayed: boolean;
  mediaDuration?: number;
  percentageComplete?: number;
  failure?: {
    code: number;
    message: string;
  };
  playedAtLeastOnce: boolean;
  fullPlayer: boolean;
  autoPlay: boolean;
}

export const initialPlayerState: PlayerState = {
  audioLoaded: false,
  audioLoading: false,
  currentlyPlaying: undefined,
  currentTime: 0,
  playing: false,
  seekTo: undefined,
  seeking: false,
  fullAudioPlayed: false,
  mediaDuration: undefined,
  percentageComplete: undefined,
  playedAtLeastOnce: false,
  fullPlayer: false,
  autoPlay: true,
};

const playerSlice = createSlice({
  name: 'player',
  initialState: initialPlayerState,
  reducers: {
    audioLoaded: (state) => {
      state.audioLoading = false;
      state.audioLoaded = true;
    },
    audioLoading: (state) => {
      state.audioLoading = true;
      state.audioLoaded = false;
      state.fullAudioPlayed = false;
    },
    playbackEnded: (state) => {
      state.playing = false;
      state.fullAudioPlayed = true;
    },
    playbackFailed: (
      state,
      action: PayloadAction<{ code: number; message: string }>,
    ) => {
      state.audioLoading = false;
      state.failure = action.payload;
      state.playing = false;
    },
    playbackPaused: (state) => {
      state.playing = false;
    },
    playbackSeeked: (state) => {
      state.seeking = false;
      state.seekTo = undefined;
    },
    playbackSeeking: (state) => {
      state.seeking = true;
    },
    playbackStarted: (state) => {
      state.playing = true;
      state.playedAtLeastOnce = true;
    },
    requestPlay: (state) => {
      state.playing = true;
    },
    requestPause: (state) => {
      state.playing = false;
    },
    requestSeekBackward: (state) => {
      state.seekTo = Math.max(state.currentTime - SEEK_DURATION, 0);
    },
    requestSeekForward: (state) => {
      state.seekTo = state.mediaDuration
        ? Math.min(state.currentTime + SEEK_DURATION, state.mediaDuration)
        : state.seekTo;
    },
    requestSeekToTime: (state, action: PayloadAction<number>) => {
      state.seekTo = action.payload;
    },
    resetFullAudioPlayed: (state) => {
      state.fullAudioPlayed = false;
    },
    updateCurrentlyPlaying: (
      state,
      action: PayloadAction<CurrentlyPlaying | undefined>,
    ) => {
      state.currentlyPlaying = action.payload;
      state.fullAudioPlayed = false;
    },
    updateCurrentTime: (state, action: PayloadAction<number>) => {
      state.currentTime = Math.round(action.payload || 0);
    },
    updateMediaDuration: (state, action: PayloadAction<number>) => {
      state.mediaDuration = action.payload;
    },
    updatePercentageCompleted: (state, action: PayloadAction<number>) => {
      state.percentageComplete = action.payload;
    },
    showFullPlayer: (state) => {
      state.fullPlayer = true;
    },
    hideFullPlayer: (state) => {
      state.fullPlayer = false;
    },
    setAutoplay: (state, action: PayloadAction<boolean>) => {
      state.autoPlay = !!action.payload;
    },
    resetPlayer: (state) => {
      return {
        ...initialPlayerState,
        fullPlayer: state.fullPlayer,
        autoPlay: state.autoPlay,
      };
    },
  },
});

// Export slice actions directly
export const {
  audioLoaded,
  audioLoading,
  playbackEnded,
  playbackFailed,
  playbackPaused,
  playbackSeeked,
  playbackSeeking,
  playbackStarted,
  requestPause,
  requestPlay,
  requestSeekBackward,
  requestSeekForward,
  requestSeekToTime,
  resetFullAudioPlayed,
  updateCurrentlyPlaying,
  updateCurrentTime,
  updateMediaDuration,
  updatePercentageCompleted,
  showFullPlayer,
  hideFullPlayer,
  setAutoplay,
  resetPlayer,
} = playerSlice.actions;

export default playerSlice.reducer;

// ACTION CREATORS

export const audioLoadedThunk = () => (dispatch, getState: GetReduxState) => {
  const {
    player: { currentlyPlaying },
  } = getState();

  if (loadStartTime !== null) {
    metricsQueue.timing('player_load_latency_ms', Date.now() - loadStartTime, {
      entity_type: getEntityType(currentlyPlaying),
      playback_type: getPlaybackType(currentlyPlaying),
    });
    loadStartTime = null;
  }

  dispatch(audioLoaded());
};

export const audioLoadingThunk = () => (dispatch) => {
  loadStartTime = Date.now();
  dispatch(audioLoading());
};

export const playbackFailedThunk =
  (code: number, message: string, source = 'media_element') =>
  (dispatch) => {
    metricsQueue.increment('player_playback_failure_total', {
      error_code: String(code),
      error_type: MEDIA_ERROR_TYPE[code] ?? 'unknown',
      source,
    });

    dispatch(playbackFailed({ code, message }));
  };

export const playbackSeekingThunk = () => (dispatch) => {
  seekStartTime = Date.now();
  currentSeekIsUserInitiated = userSeekPending;
  userSeekPending = false;
  dispatch(playbackSeeking());
};

export const requestSeekToTimeThunk = (seekTo: number) => (dispatch) => {
  userSeekPending = true;
  dispatch(requestSeekToTime(seekTo));
};

// SIDE EFFECTS

export const playbackEndedThunk = () => (dispatch, getState: GetReduxState) => {
  const {
    player: { currentTime, currentlyPlaying },
  } = getState();

  const isTeaser = currentlyPlaying?.audioType === 'teaser';
  const eventName = isTeaser ? 'complete-teaser' : 'complete';

  trackPlayerEvent({
    eventName,
    currentTime,
    currentlyPlaying,
    isTeaser,
  });

  metricsQueue.increment('player_playback_ended_total', {
    entity_type: getEntityType(currentlyPlaying),
    playback_type: getPlaybackType(currentlyPlaying),
  });

  dispatch(playbackEnded());
};

export const playbackPausedThunk =
  () => (dispatch, getState: GetReduxState) => {
    const {
      player: { currentTime, currentlyPlaying, mediaDuration },
    } = getState();

    if (mediaDuration && currentTime !== Math.round(mediaDuration)) {
      trackPlayerEvent({ eventName: 'pause', currentTime, currentlyPlaying });
    }

    dispatch(playbackPaused());
  };

export const playbackSeekedThunk =
  () => (dispatch, getState: GetReduxState) => {
    const {
      player: { currentlyPlaying, currentTime },
    } = getState();

    if (currentlyPlaying) {
      const { storyTitle } = currentlyPlaying;

      analytics.track('seek', {
        category: 'player',
        label: storyTitle,
        Position: currentTime,
      });
    }

    if (seekStartTime !== null) {
      const duration = Date.now() - seekStartTime;
      const metricName = currentSeekIsUserInitiated
        ? 'player_seek_latency_ms'
        : 'player_stall_duration_ms';

      metricsQueue.timing(metricName, duration, {
        entity_type: getEntityType(currentlyPlaying),
        playback_type: getPlaybackType(currentlyPlaying),
      });
      seekStartTime = null;
      currentSeekIsUserInitiated = false;
    }

    dispatch(playbackSeeked());
  };

export const playbackStartedThunk =
  () => (dispatch, getState: GetReduxState) => {
    const {
      player: { currentTime, currentlyPlaying, playedAtLeastOnce },
      profile: { user },
    } = getState();

    const isTeaser = currentlyPlaying?.audioType === 'teaser';
    const eventName = playedAtLeastOnce ? 'continue' : 'start';
    const trackingId = user?.trackingId;

    trackPlayerEvent({ eventName, currentTime, currentlyPlaying, isTeaser });

    metricsQueue.increment('player_playback_started_total', {
      entity_type: getEntityType(currentlyPlaying),
      playback_type: getPlaybackType(currentlyPlaying),
    });

    if (currentlyPlaying && !isTeaser) {
      sendSearchInsightBuilder('viewedObjectIDs', {
        userToken: trackingId,
        index: 'Stories',
        eventName: 'story_playback_started',
        objectIDs: [currentlyPlaying?.id],
      })();
    }

    dispatch(playbackStarted());
  };

export const requestPlayThunk = () => async (dispatch) => {
  const current = document.getElementsByClassName(
    'connectedAudioPlayerAudioTag',
  )[0] as HTMLAudioElement;

  lastTrackedPercentage = 0;

  if (current && current.paused) {
    try {
      await current.play();
      dispatch(requestPlay());
    } catch (error) {
      const domError = error as DOMException;
      const code = domError?.code ?? 0;
      const message = domError?.message ?? String(error);

      dispatch(playbackFailedThunk(code, message, 'play_promise'));
    }
  }
};

export const requestPauseThunk = () => (dispatch: AppDispatch) => {
  const current = document.getElementsByClassName(
    'connectedAudioPlayerAudioTag',
  )[0] as HTMLAudioElement;

  if (!current) {
    return;
  }

  if (!current.paused) {
    current.pause();
  }

  dispatch(requestPause());
};

export const requestSeekBackwardThunk =
  () => (dispatch, getState: GetReduxState) => {
    const {
      player: { currentTime, currentlyPlaying },
    } = getState();

    trackPlayerEvent({ eventName: 'rewind', currentTime, currentlyPlaying });

    userSeekPending = true;
    dispatch(requestSeekBackward());
  };

export const requestSeekForwardThunk =
  () => (dispatch, getState: GetReduxState) => {
    const {
      player: { currentTime, currentlyPlaying },
    } = getState();

    trackPlayerEvent({ eventName: 'forward', currentTime, currentlyPlaying });

    userSeekPending = true;
    dispatch(requestSeekForward());
  };

export const requestTogglePlayThunk =
  () => (dispatch, getState: GetReduxState) => {
    const action = getState().player.playing
      ? requestPauseThunk()
      : requestPlayThunk();

    // Avoid race condition on some browsers
    setTimeout(() => {
      dispatch(action);
    }, 200);
  };

const getEntityType = (
  currentlyPlaying: CurrentlyPlaying | undefined,
): string => {
  if (!currentlyPlaying) return 'unknown';
  return currentlyPlaying.type;
};

const getPlaybackType = (
  currentlyPlaying: CurrentlyPlaying | undefined,
): string => {
  if (!currentlyPlaying) return 'unknown';
  return currentlyPlaying.audioType === 'teaser' ? 'teaser' : 'full_audio';
};

export const resetPlayerThunk = () => (dispatch, getState: GetReduxState) => {
  const {
    player: { currentlyPlaying, playedAtLeastOnce },
  } = getState();

  if (playedAtLeastOnce && !reached25Pct && currentlyPlaying) {
    metricsQueue.increment('player_early_abandonment_total', {
      entity_type: getEntityType(currentlyPlaying),
      playback_type: getPlaybackType(currentlyPlaying),
    });
  }

  reached25Pct = false;
  loadStartTime = null;
  seekStartTime = null;
  userSeekPending = false;
  currentSeekIsUserInitiated = false;
  dispatch(requestPauseThunk());
  dispatch(resetPlayer());
};

const getDurationLabel = (durationTime) =>
  durationTime < REQUIRED_STORY_DURATION
    ? LESS_THAN_REQUIRED_DURATION
    : GREATER_THAN_REQUIRED_DURATION;

export const updateCurrentTimeThunk =
  (currentTime) => (dispatch, getState: GetReduxState) => {
    const {
      player: { currentlyPlaying, mediaDuration, percentageComplete },
      profile: { user },
    } = getState();

    if (mediaDuration) {
      const isTeaser = currentlyPlaying?.audioType === 'teaser';
      const trackingId = user?.trackingId;
      const percentage = Math.round((currentTime / mediaDuration) * 100);

      if (percentage >= 25 && !reached25Pct) {
        reached25Pct = true;
      }

      if (percentage !== percentageComplete) {
        dispatch(updatePercentageCompleted(percentage));

        if (
          currentlyPlaying &&
          !isTeaser &&
          percentage >= 50 &&
          percentage % 10 === 0
        ) {
          sendSearchInsightBuilder('viewedObjectIDs', {
            userToken: trackingId,
            index: 'Stories',
            eventName: `story_played_${percentage}_percent`,
            objectIDs: [currentlyPlaying?.id],
          })();
        }
      }

      if (currentlyPlaying && shouldTrackAudioEvent(percentage)) {
        lastTrackedPercentage = percentage;

        trackAudioEvent({
          audioAction: `reach-${percentage}`,
          audioName: currentlyPlaying.storyTitle || '',
          audioSpeaker: currentlyPlaying.speakerName,
          isTeaser,
          audioUrl: currentlyPlaying.audioUrl,
        });
      }
    }

    femtasySessionStorage.setItem(
      PLAYER_DURATION_LABEL_STORED,
      getDurationLabel(currentTime),
    );

    dispatch(updateCurrentTime(currentTime));
  };

export const updateMediaDurationThunk = (duration) => (dispatch) => {
  dispatch(updateMediaDuration(duration));
  dispatch(requestPlayThunk());
};

export const setAutoplayThunk = (autoplayEnabled: boolean) => (dispatch) => {
  femtasyLocalStorage.setItem(FEMTASY_AUTOPLAY, autoplayEnabled);

  dispatch(setAutoplay(autoplayEnabled));
};

const trackAudioEvent = ({
  audioAction,
  audioName,
  audioSpeaker,
  isTeaser,
  audioUrl,
}) => {
  tracker.sendEvent(
    'audio',
    [
      {
        audioAction,
        audioName,
        audioSpeaker,
        audioUrl,
        audioType: isTeaser ? 'teaser' : 'full-audio',
        pageUrl: window.location ? window.location.href : '',
      },
    ],
    !isProduction(),
    [dataLayerProvider],
  );
};

const shouldTrackAudioEvent = (percentage: number) => {
  return (
    PERCENTAGES_TO_TRACK.includes(percentage) &&
    percentage !== lastTrackedPercentage
  );
};
